'use client';

import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface NavigationBarProps {
  currentStep: number;
  canProceed: boolean;
  isLoading: boolean;
  onNext: () => void;
  onBack: () => void;
  nextLabel: string;
  backLabel: string;
  loadingLabel: string;
  showBack: boolean;
  hidden: boolean;
  isKeyboardOpen: boolean;
  /** Step label for mobile progress indicator (e.g. "Your details") */
  stepLabel?: string;
  /** Skip handler — shown as outline button next to primary CTA on step 4 */
  onSkip?: () => void;
  /** Desktop label for skip button (e.g. "Skip & Complete") */
  skipLabel?: string;
  /** Mobile label for skip button — shorter for tight space (e.g. "Skip") */
  skipLabelMobile?: string;
}

const primaryCta =
  'bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Step navigation: desktop row + fixed mobile bottom bar with progress line.
 * Lifted from dgr/register with semantic tokens.
 */
export function NavigationBar({
  currentStep,
  canProceed,
  isLoading,
  onNext,
  onBack,
  nextLabel,
  backLabel,
  loadingLabel,
  showBack,
  hidden,
  isKeyboardOpen,
  stepLabel,
  onSkip,
  skipLabel,
  skipLabelMobile,
}: NavigationBarProps) {
  if (hidden) return null;

  return (
    <>
      {/* Desktop */}
      <div className="hidden md:mt-6 md:flex md:items-center md:justify-between md:border-t md:border-border md:pt-4">
        {showBack ? (
          <Button
            type="button"
            variant="ghost"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
            className="text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {backLabel}
          </Button>
        ) : (
          <div />
        )}

        {currentStep < 4 ? (
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onNext();
            }}
            disabled={!canProceed || isLoading}
            className={`px-8 ${primaryCta}`}
          >
            {nextLabel}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <div className="flex items-center gap-3">
            {onSkip && skipLabel && (
              <Button
                type="button"
                variant="outline"
                onClick={onSkip}
                disabled={!canProceed || isLoading}
                className="px-6 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              >
                {skipLabel}
              </Button>
            )}
            <Button
              type="button"
              disabled={!canProceed || isLoading}
              onClick={onNext}
              className={`px-8 ${primaryCta}`}
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {loadingLabel}
                </span>
              ) : (
                nextLabel
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Mobile */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 flex flex-col border-t border-border bg-background/95 px-4 pt-3 pb-safe backdrop-blur sm:px-6 md:hidden ${isKeyboardOpen ? 'gap-2' : 'gap-2.5'}`}
      >
        {/* Progress bar */}
        {stepLabel && (
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {currentStep}/4
            </span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${(currentStep / 4) * 100}%` }}
              />
            </div>
            <span className="truncate text-xs text-muted-foreground">{stepLabel}</span>
          </div>
        )}

        {/* Buttons — single row */}
        <div className="flex items-center gap-2.5">
          {showBack ? (
            <Button
              type="button"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                onBack();
              }}
              className="shrink-0 px-4 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {backLabel}
            </Button>
          ) : null}

          {currentStep < 4 ? (
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onNext();
              }}
              disabled={!canProceed || isLoading}
              className={`min-w-0 flex-1 justify-center whitespace-normal px-6 py-3 text-center ${primaryCta}`}
            >
              {nextLabel}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <>
              {onSkip && skipLabelMobile && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSkip}
                  disabled={!canProceed || isLoading}
                  className="shrink-0 px-4 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {skipLabelMobile}
                </Button>
              )}
              <Button
                type="button"
                disabled={!canProceed || isLoading}
                onClick={onNext}
                className={`min-w-0 flex-1 justify-center whitespace-normal px-6 py-3 text-center ${primaryCta}`}
              >
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {loadingLabel}
                  </span>
                ) : (
                  nextLabel
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
